import { z } from "zod";

import { daysEnum } from "../../../enums/days.enum";
import { monthsEnum } from "../../../enums/months.enum";
import {
  ServiceCategory,
  ServiceSubCategory,
  ServiceTypeEnum,
  ServiceRequestTypeEnum,
  ServiceRequestUnitEnum,
  CurrencyEnum,
  ProgramLevelEnum,
} from "../enum/service.enum";

import { entityRoleEnum } from "../../../enums/entity.enum";

const serviceAvailabilityOptionEnum = z.enum(["online", "offline"], {
  required_error: "SERVICE_AVAILABILITY_OPTION_REQUIRED",
});

export const availabilitySchema = z
  .object({
    days: z.array(daysEnum, { required_error: "DAYS_AVAILABILITY_REQUIRED" }).default([]).optional(),
    months: z.array(monthsEnum, { required_error: "MONTHS_AVAILABILITY_REQUIRED" }).default([]).optional(),
    from: z.string({ required_error: "FROM_REQUIRED", invalid_type_error: "FROM_REQUIRED" }).optional(),
    to: z.string({ required_error: "TO_REQUIRED", invalid_type_error: "TO_REQUIRED" }).optional(),
    option: serviceAvailabilityOptionEnum,
    locations: z.array(z.number(), { required_error: "LOCATIONS_REQUIRED" }).default([]).optional(),
    alwaysAvailable: z.boolean().default(false),
    availableAllDay: z.boolean().default(false),
  })
  .superRefine((data, ctx) => {
    if (!data.alwaysAvailable) {
      if (!data.days?.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "DAYS_AVAILABILITY_REQUIRED",
          path: ["days"],
        });
      }
      if (!data.months?.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "MONTHS_AVAILABILITY_REQUIRED",
          path: ["months"],
        });
      }
      if (!data.locations?.length) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "LOCATIONS_REQUIRED",
          path: ["locations"],
        });
      }

      if (!data.availableAllDay) {
        if (!data.from) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "FROM_REQUIRED",
            path: ["from"],
          });
        }
        if (!data.to) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "TO_REQUIRED",
            path: ["to"],
          });
        }
      }
    }
  });

export const ageRangeSchema = z.object({
  minAge: z.number().min(0).default(0),
  maxAge: z.number().min(0).default(0),
});

export const customerPreferenceConfigSchema = z.object({
  ageRange: ageRangeSchema.default({}),
  personas: z
    .array(z.nativeEnum(entityRoleEnum), { required_error: "PERSONAS_REQUIRED" })
    .min(1, { message: "PERSONAS_REQUIRED" })
    .default([]),
  restrict: z.boolean().default(true),
  locations: z
    .array(z.number(), { required_error: "CUSTOMER_LOCATIONS_REQUIRED" })
    .min(1, { message: "CUSTOMER_LOCATIONS_REQUIRED" })
    .default([]),
});

export const paymentConfigSchema = z
  .object({
    type: z.nativeEnum(ServiceRequestTypeEnum, { required_error: "PAYMENT_CONFIG_TYPE_REQUIRED" }),
    unit: z.nativeEnum(ServiceRequestUnitEnum, { required_error: "UNIT_REQUIRED" }).optional(),
    amount: z.coerce.number({ required_error: "AMOUNT_REQUIRED", invalid_type_error: "AMOUNT_REQUIRED" }),
    currency: z.nativeEnum(CurrencyEnum),
    expiryDate: z.coerce.date({ required_error: "EXPIRY_DATE_REQUIRED" }).optional(),
    notExpired: z.boolean().nullable().optional().default(false),
    allowGoalngoMemebers: z.literal(true),
    agreeToTerms: z.literal(true),
  })
  .superRefine((data, ctx) => {
    if (data.type === ServiceRequestTypeEnum.Unit && !data.unit) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "UNIT_REQUIRED",
        path: ["unit"],
      });
    }

    if (data.notExpired === false && !data.expiryDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "EXPIRY_DATE_REQUIRED",
        path: ["expiryDate"],
      });
    }
  });

export const mediaSchema = z.object(
  {
    // id: z.number().optional(),
    url: z.string(),
    type: z.enum(["IMAGE", "VIDEO"]),
    thumbnail: z.string().optional(),
    duration: z.number().optional(),
  },
  { required_error: "MEDIA_REQUIRED", invalid_type_error: "MEDIA_REQUIRED" }
);

export const programContentSchema = z.object({
  videoTitle: z.string({ required_error: "VIDEO_TITLE_REQUIRED" }),
  // videoDescription: z.string({ required_error: "VIDEO_DESCRIPTION_REQUIRED" }),
  duration: z.number(),
  isFree: z.boolean().default(false),
  media: mediaSchema,
  instructions: z.string({ required_error: "VIDEO_DESCRIPTION_REQUIRED" }),
  equipments: z.array(z.number()),
});

export const serviceInformationSchema = z.object({
  type: z.literal(ServiceTypeEnum.OneTime),
  name: z.string({ required_error: "NAME_REQUIRED" }),
  description: z.string({ required_error: "DESCRIPTION_REQUIRED" }),
  category: z.nativeEnum(ServiceCategory, { required_error: "CATEGORY_REQUIRED" }),
  subCategory: z.nativeEnum(ServiceSubCategory, { required_error: "SUB_CATEGORY_REQUIRED" }),
  banner: z.number({ required_error: "BANNER_REQUIRED" }),
});
export const serviceDeliverySchema = availabilitySchema;
export const serviceCustomerPreferenceSchema = customerPreferenceConfigSchema;
export const servicePaymentConfigSchema = paymentConfigSchema;

export const createServiceSchema = z
  .object({
    availability: serviceDeliverySchema,
    customerPreferenceConfig: serviceCustomerPreferenceSchema,
    paymentConfig: servicePaymentConfigSchema,
  })
  .merge(serviceInformationSchema);

export const programInformationSchema = z.object({
  banner: z.number({ required_error: "BANNER_REQUIRED" }),
  type: z.literal(ServiceTypeEnum.Program),
  name: z.string({ required_error: "NAME_REQUIRED" }),
  description: z.string({ required_error: "DESCRIPTION_REQUIRED" }),
  category: z.nativeEnum(ServiceCategory, { required_error: "CATEGORY_REQUIRED" }),
  subCategory: z.nativeEnum(ServiceSubCategory, { required_error: "SUB_CATEGORY_REQUIRED" }),
  level: z.nativeEnum(ProgramLevelEnum, { required_error: "LEVEL_REQUIRED" }),
});

export const ProgramPaymentConfigSchema = z.object({
  type: z.nativeEnum(ServiceRequestTypeEnum, { required_error: "PAYMENT_CONFIG_TYPE_REQUIRED" }),
  amount: z.coerce.number({ required_error: "AMOUNT_REQUIRED", invalid_type_error: "AMOUNT_REQUIRED" }),
  currency: z.nativeEnum(CurrencyEnum),
  notExpired: z.boolean().nullable().optional().default(false),
});

export const createProgramSchema = z
  .object({
    programContents: z.array(programContentSchema),
    availability: availabilitySchema.optional(),
    idealCompletionTime: z.coerce
      .number({
        required_error: "IDEAL_COMPLETION_TIME_REQUIRED",
        invalid_type_error: "NUMBER_VALUE_IS_REQUIRED",
      })
      .int()
      .positive(),
    numberOfVideos: z.coerce
      .number({ required_error: "NUMBER_OF_VIDEOS_REQUIRED", invalid_type_error: "NUMBER_VALUE_IS_REQUIRED" })
      .int()
      .min(2, { message: "ADD_AT_LEAST_2_VIDEOS" })
      .positive(),
    paymentConfig: ProgramPaymentConfigSchema,
    allowGoalngoMemebers: z.literal(true).optional(),
    agreeToTerms: z
      .boolean({
        required_error: "TERMS_AND_CONDITIONS_REQUIRED",
      })
      .refine((val) => val === true, {
        message: "TERMS_AND_CONDITIONS_REQUIRED",
      }),
  })
  .merge(programInformationSchema);
